import { BotId } from "../../global/models/models";

export type AnswerBy =
    | MultipleChoice
    | FormInput
    | TextInput
    | SalaryInput
    | Suggestions
    | CookiesAnswerBy
    | Dropdown
    | FormDropdown
    | BBOT
    | undefined;

export type BotMessage =
    | DefaultText
    | InfoText
    | TimeoutText
    | CollapsibleList
    | TwoColumnImages;

export type BotMessages = BotMessage[];

export interface ReplyParsed {
    messages: BotMessages;
    answerBy: AnswerBy;
    botId?: BotId;
    clientID?: string;
    status?: number;
}
// messages
export interface DefaultText {
    type: "defaultText";
    text: string;
    bubbleId: string;
    feedback?: boolean;
}
export interface TimeoutText {
    type: "timeoutText";
    text: string;
    bubbleId: string;
    feedback?: boolean;
}
export interface InfoText {
    type: "infoText";
    text: string;
    info: string;
    bubbleId: string;
    feedback?: boolean;
}
export interface Collapsible {
    text: string;
    collapsed: string;
}
export interface CollapsibleList {
    text?: string;
    type: "collapsible";
    elements: Collapsible[];
    bubbleId: string;
    feedback?: boolean;
}
export interface TwoColumnImages {
    type: "twoColumnImages";
    text: string;
    bubbleId: string;
    feedback?: boolean;
    img?: string;
}
// answerBy
export interface CookiesAnswerBy {
    type: "cookies";
    elements: (CookieButton | CookieLink)[];
}

export interface CookieButton {
    type: "button";
    text: string;
    payload: string;
    screenReader?: string;
}

export interface CookieLink {
    type: "link";
    text: string;
    payload?: string;
    url: string;
    screenReader?: string;
    cookies: {
        // domain could be otv_prefill for example
        [domain: string]: {
            [cookieKey: string]: string;
        };
    };
}

export interface Choice {
    title: string;
    payload: string;
    botId?: BotId;
    german?: string;
    tooltipp?: string;
}
export interface MultipleChoice {
    type: "multipleChoice";
    choices: Choice[];
    shortLabels?: boolean;
    inputDisabled?: boolean;
}

export interface Dropdown {
    type: "dropdown";
    text: string;
    choices: Choice[];
    bubbleId: string;
    feedback?: boolean;
}

export interface TextInput {
    type: "textInput";
    placeholder?: string;
}

export interface FormInput {
    type: 'formInput'
    elements: {
        placeholder?: string
        infoString?: string
        payload: string
        name: string
        type: FormFieldType
        title: string
        pageName: string
        choice?: Choice[]
        suggestion?: Suggestion[]
        keyValue: string
        german: string
        theme?: string
        icon: string
        stage?: string
    }[]
}

export interface SalaryInput {
    type: "salaryInput";
    placeholder?: string;
}
export interface LinkSuggestion {
    type: 'link'
    title: string
    payload: string
    url: string
    screenReader?: string
    fullWidth?: boolean
    german?: string
    theme?: string
}
export interface FormSuggestion {
    type: 'form'
    title: string
    payload: string
    url: string
    screenReader?: string
    fullWidth?: boolean
    german?: string
    theme?: string
}
export interface ButtonSuggestion {
    type: 'button'
    title: string
    payload: string
    screenReader?: string
    fullWidth?: boolean
    botId?: boolean
    changeBotTheme?: boolean
    german?: string
    theme?: string
}
export interface Suggestions {
    type: 'suggestions'
    placeholder?: string
    elements: (LinkSuggestion | ButtonSuggestion | FormSuggestion)[]
    introText?: boolean
}

export interface BBOT {
    type: 'BBOT'
    placeholder?: 'string'
    inputDisabled?: boolean
}

// Messages that are part of conversation, but never come from backend
export interface UserMessage {
    type: "userMessage";
    text: string;
}
// After clicking on a LeichteSpracheLinkSuggestions of answerBy, we need to add the link to the conversation, but no payload is sent on a second click
export type ConversationLink = Omit<LinkSuggestion, "payload" | "type"> & {
    type: "conversationLink";
};

export type ConversationCookieLink = Omit<CookieLink, "payload" | "type"> & {
    type: "conversationCookieLink";
};

export type ConversationDropdown = Omit<Dropdown, "type"> & {
    type: "conversationDropdown";
    selectedOptionLabel: string | undefined;
};

export interface FormThemeStamp {
    type: "formThemeStamp";
    botId: BotId;
}
export type FormDropdown = Omit<Dropdown, 'type'> & {
    type: 'formDropdown'
    selectedOptionLabel: string | undefined
}

export interface StartThemeStamp {
    type: "startThemeStamp";
    botId: BotId;
    message?: string;
}

export interface FeedbackGivenStamp {
    type: "feedbackGivenStamp";
}

export interface FeedbackWithdrawnStamp {
    type: "feedbackWithdrawnStamp";
}

export enum StartThemeStampLabels {
    KUG = "Kurzarbeitergeld",
    ALG = "Arbeitslosengeld",
    FAMKA = "Kinderzuschlag",
    BGELD = "Bürgergeld",
    KIN = "Kindergeld",
    SAS = "Ausbildung, Studium & Berufe",
    PINK = "Unternehmensportal",
    FORM = "Formularassistent",
    INKASSO = "Inkasso",
}

export enum FormFieldType {
    FORM_INPUT = 'formInput',
    FORM_TEXT = 'formText',
    BUTTON = 'button',
    RADIO = 'radioButton',
    SUBMIT = 'formButton',
    DROPDOWN = 'formDropdown',
    DATEPICKER = 'formDate',
    TABLE = 'formTable',
}

export type StampText =
    | StartThemeStamp
    | FeedbackGivenStamp
    | FeedbackWithdrawnStamp
    | FormThemeStamp;

export type Suggestion = LinkSuggestion | ButtonSuggestion | ConversationLink | FormSuggestion

export type ConversationMessage =
    | DefaultText
    | InfoText
    | TimeoutText
    | CollapsibleList
    | TwoColumnImages
    | UserMessage
    | ConversationLink
    | ConversationCookieLink
    | ConversationDropdown
    | FormInput
    | FormDropdown;

export type Conversation = (ConversationMessage | StampText)[];
